import { useState, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Tenant, type Server, type ActivityEntry, type TenantSettingField, type Tier, type SweeperConfigResponse } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, statusTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { DeleteModeOption } from '@/components/DeleteModeOption';
import { Input, Label } from '@/components/ui/input';
import { Toggle } from '@/components/ui/toggle';
import { PostponeDialog } from '@/components/PostponeDialog';
import { Copy, Download, ExternalLink, Eye, LayoutGrid, Pause, Play, RefreshCw, Sprout, Trash2, KeyRound, UserX, X } from 'lucide-react';
import { formatDate, relativeTime, removalDueLabel, loginDaysLeft } from '@/lib/utils';
import { ModesSummary } from '@/components/ModesEditor';
import { TenantSkillsCard } from '@/components/TenantSkillsCard';
import type { Mode } from '@/lib/modes';

export function TenantDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const tenant = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => api.get<{ tenant: Tenant }>(`/api/tenants/${id}`),
    refetchInterval: 5000,
  });

  const server = useQuery({
    queryKey: ['server', tenant.data?.tenant.server_id ?? ''],
    queryFn: () => api.get<{ server: Server }>(`/api/servers/${tenant.data!.tenant.server_id}`),
    enabled: !!tenant.data,
  });

  const activity = useQuery({
    queryKey: ['activity', { target_kind: 'tenant', target_id: id }],
    queryFn: () =>
      api.get<{ activity: ActivityEntry[] }>(
        `/api/activity?target_kind=tenant&target_id=${encodeURIComponent(id)}&limit=50`
      ),
  });

  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [suspendKind, setSuspendKind] = useState<'hard' | 'payment_required'>('hard');
  const suspend = useMutation({
    mutationFn: (kind: 'hard' | 'payment_required') =>
      api.post(`/api/tenants/${id}/suspend`, { kind }),
    onSuccess: () => {
      setConfirmSuspend(false);
      qc.invalidateQueries({ queryKey: ['tenant', id] });
    },
  });
  // No body — the backend auto-resolves from the stored suspend_kind (docker
  // start after a hard suspend, payment-hold clear after a soft one).
  const resume = useMutation({
    mutationFn: () => api.post(`/api/tenants/${id}/resume`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', id] }),
  });
  const remove = useMutation({
    mutationFn: (mode: 'cp_only' | 'stop' | 'stop_purge') =>
      api.del(`/api/tenants/${id}?mode=${mode}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
      navigate('/tenants');
    },
  });
  // Manual probe — the only thing in the CP that reaches into a container to
  // read live auth. Everything automatic (this page, the tenants list, the
  // health poll) reads the host-side credentials sweep instead, because a live
  // check travels into the container and would wake a stopped one. One request:
  // /health-check runs the HTTP /health probe and both providers' auth
  // reconciliation. The 5s page poll picks up the refreshed row.
  const recheck = useMutation({
    mutationFn: () => api.post(`/api/tenants/${id}/health-check`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', id] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['tenant-ops-events', id] });
    },
  });

  const [claudeAuthOpen, setClaudeAuthOpen] = useState(false);
  const [codexAuthOpen, setCodexAuthOpen] = useState(false);
  const [anthropicKeyOpen, setAnthropicKeyOpen] = useState(false);
  const [openaiKeyOpen, setOpenaiKeyOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<'claude' | 'codex' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReseed, setConfirmReseed] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'cp_only' | 'stop' | 'stop_purge'>('stop');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [dashboardEmbedUrl, setDashboardEmbedUrl] = useState<string | null>(null);
  const [tokenOpen, setTokenOpen] = useState(false);
  const revealToken = useMutation({
    mutationFn: () => api.get<{ admin_token: string }>(`/api/tenants/${id}/admin-token`),
  });
  // Disconnect subscription (OAuth) auth for one provider — clears credentials
  // on the container and resets CP state to 'none'. Confirmed via the dialog
  // below. OAuth only; API keys are managed in their own dialogs.
  const disconnect = useMutation({
    mutationFn: (provider: 'claude' | 'codex') =>
      api.post(`/api/tenants/${id}/${provider}-auth-logout`),
    onSuccess: () => {
      setDisconnectTarget(null);
      qc.invalidateQueries({ queryKey: ['tenant', id] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
  // Re-run the creation-time settings seed (one PATCH /admin/settings pushing
  // every CP-global field). Repairs a tenant whose seed lost the Traefik
  // route-propagation race at creation and so has no settings.json. Confirmed
  // via the dialog below since it overwrites tenant-side customizations.
  const reseed = useMutation({
    mutationFn: () => api.post<{ tenant: Tenant }>(`/api/tenants/${id}/reseed`),
    onSuccess: () => {
      setConfirmReseed(false);
      qc.invalidateQueries({ queryKey: ['tenant', id] });
      qc.invalidateQueries({ queryKey: ['tenant-settings', id] });
      qc.invalidateQueries({ queryKey: ['activity', { target_kind: 'tenant', target_id: id }] });
    },
  });

  // CORS on the tenant orchestrator only allows registered WP origins, so
  // the CP UI can't hit /plugin/update-check from the browser — fetch it
  // via the CP backend instead. The returned download_url is direct to
  // the tenant (Content-Disposition: attachment on the ZIP makes the
  // browser save the file instead of navigating away when clicked).
  const pluginInfo = useQuery({
    queryKey: ['plugin-info', id],
    queryFn: () =>
      api.get<{ version: string; download_url: string; download_url_no_license?: string | null }>(
        `/api/tenants/${id}/plugin-info`
      ),
    enabled: !!id && (tenant.data?.tenant.status === 'awaiting_auth' || tenant.data?.tenant.status === 'active'),
    retry: false,
    staleTime: 60_000,
  });

  const openDashboard = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; proxy_url: string }>(`/api/tenants/${id}/dashboard/open`),
    onSuccess: (data) => {
      setDashboardError(null);
      // Embed inline. The proxy injects X-Forwarded-Prefix so the dashboard's
      // absolute links/forms/redirects resolve back through /tenants/<id>/dashboard
      // instead of trying to hit the CP origin directly.
      setDashboardEmbedUrl(data.proxy_url);
    },
    onError: (err: Error) => setDashboardError(err.message),
  });

  useEffect(() => {
    if (!dashboardEmbedUrl) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDashboardEmbedUrl(null);
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the overlay is up.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [dashboardEmbedUrl]);

  // Grace-period removal (Cleanup page). The sweeper config supplies the grace
  // days, whether the automatic sweeper (which does the removal) is on, and
  // whether a "removed" webhook will fire on delete.
  const sweeper = useQuery({
    queryKey: ['sweeper-config'],
    queryFn: () => api.get<SweeperConfigResponse>('/api/sweeper/config'),
  });
  const [confirmMark, setConfirmMark] = useState(false);
  const [postponeOpen, setPostponeOpen] = useState(false);
  const [postponeDays, setPostponeDays] = useState(10);
  const removalChanged = () => {
    qc.invalidateQueries({ queryKey: ['tenant', id] });
    qc.invalidateQueries({ queryKey: ['tenants'] });
    qc.invalidateQueries({ queryKey: ['sweeper-marked'] });
    qc.invalidateQueries({ queryKey: ['activity', { target_kind: 'tenant', target_id: id }] });
  };
  // The "Never delete" lock. Turning it on also clears any pending removal mark
  // (server side), so the removal banner disappears with it.
  const setNeverDelete = useMutation({
    mutationFn: (never_delete: boolean) =>
      api.post<{ tenant: Tenant }>(`/api/tenants/${id}/never-delete`, { never_delete }),
    onSuccess: removalChanged,
  });

  const markRemoval = useMutation({
    mutationFn: () => api.post<{ tenant: Tenant }>(`/api/tenants/${id}/removal/mark`),
    onSuccess: () => {
      setConfirmMark(false);
      removalChanged();
    },
  });
  const postponeRemoval = useMutation({
    mutationFn: (days: number) => api.post<{ tenant: Tenant }>(`/api/tenants/${id}/removal/postpone`, { days }),
    onSuccess: () => {
      setPostponeOpen(false);
      removalChanged();
    },
  });
  const unmarkRemoval = useMutation({
    mutationFn: () => api.post<{ tenant: Tenant }>(`/api/tenants/${id}/removal/unmark`),
    onSuccess: removalChanged,
  });

  const tiers = useQuery({
    queryKey: ['tiers'],
    queryFn: () => api.get<{ tiers: Tier[] }>('/api/tiers'),
  });
  const changeTier = useMutation({
    mutationFn: (tier: string) =>
      api.patch<{ tenant: Tenant; pushed: boolean; push_reason: string | null }>(
        `/api/tenants/${id}/tier`,
        { tier }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', id] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['tiers'] });
    },
  });

  if (tenant.isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (tenant.isError || !tenant.data) return <div className="text-sm text-red-600">Tenant not found.</div>;

  const t = tenant.data.tenant;
  // The container is up and admin endpoints answer in both awaiting_auth and
  // active. AI-subscription OAuth only gates chat sessions inside the tenant —
  // the dashboard tunnel, license CRUD, settings PATCH, plugin downloads, etc.
  // all work fine without it. So actions that only need a live container key
  // off this, not strictly off `status === 'active'`.
  const isContainerRunning = t.status === 'awaiting_auth' || t.status === 'active';
  const canonicalUrl = server.data
    ? `https://${t.id}.${server.data.server.region}.wpgodmode.com`
    : null;
  const graceDays = sweeper.data?.config.grace_days ?? 10;
  const sweeperOn = sweeper.data?.config.enabled ?? false;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/tenants" className="text-xs text-slate-500 hover:text-slate-700">
          ← Back to tenants
        </Link>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 font-mono">{t.id}</h1>
          <Badge tone={statusTone(t.status)}>{t.status}</Badge>
          {t.status === 'suspended' && t.suspend_kind === 'payment_required' && (
            <Badge tone="warning" className="whitespace-nowrap">payment hold</Badge>
          )}
          <Badge tone={statusTone(t.claude_oauth_status)}>claude: {t.claude_oauth_status}</Badge>
          {t.claude_oauth_status !== 'expired' && loginDaysLeft(t.claude_login_expires_at) !== null && (
            <Badge tone="warning" className="whitespace-nowrap">
              claude login ends in {loginDaysLeft(t.claude_login_expires_at)}d
            </Badge>
          )}
          <Badge tone={statusTone(t.codex_oauth_status)}>codex: {t.codex_oauth_status}</Badge>
          {(t.anthropic_api_key_set || t.openai_api_key_set) && (
            <Badge tone="info">
              {[t.anthropic_api_key_set && 'anthropic', t.openai_api_key_set && 'openai']
                .filter(Boolean)
                .join(' + ')}{' '}
              key
            </Badge>
          )}
          {t.override_count > 0 && (
            <Badge tone="info">
              {t.override_count} {t.override_count === 1 ? 'override' : 'overrides'}
            </Badge>
          )}
        </div>
        {canonicalUrl && (
          <a
            href={canonicalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-slate-600 hover:text-slate-900 mt-1 inline-flex items-center gap-1.5 font-mono break-all"
          >
            {canonicalUrl} <ExternalLink size={12} className="shrink-0" />
          </a>
        )}
      </div>

      {t.removal_marked_at && t.removal_due_at && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="font-medium">
              Marked for removal —{' '}
              {removalDueLabel(t.removal_due_at) === 'due'
                ? 'due now'
                : `removal ${removalDueLabel(t.removal_due_at)}`}{' '}
              ({formatDate(t.removal_due_at)})
            </div>
            <div className="text-xs text-red-800 mt-0.5">
              {removalReason(t)} At the due date the container is stopped and all data purged
              {!sweeperOn && ' — but the automatic sweeper is off, so nothing happens until it is turned on'}.
            </div>
            {(postponeRemoval.isError || unmarkRemoval.isError) && (
              <div className="text-xs text-red-700 mt-1">
                {((postponeRemoval.error ?? unmarkRemoval.error) as Error).message}
              </div>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                postponeRemoval.reset();
                setPostponeDays(graceDays);
                setPostponeOpen(true);
              }}
            >
              Postpone…
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => unmarkRemoval.mutate()}
              disabled={unmarkRemoval.isPending}
              title="Clear the mark. If the account still matches a sweep rule, the next automatic run marks it again — postpone to leave it alone for a while."
            >
              Unmark
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Identity</CardTitle>
          </CardHeader>
          <CardBody className="text-sm space-y-2">
            <Row label="Server" value={t.server_id} link={`/servers/${t.server_id}`} />
            <div className="flex items-center justify-between gap-4">
              <span className="text-slate-500 text-xs uppercase tracking-wide">Tier</span>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <select
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                  value={t.tier ?? ''}
                  disabled={changeTier.isPending}
                  onChange={(e) => changeTier.mutate(e.target.value)}
                >
                  {!tiers.data?.tiers.some((x) => x.id === t.tier) && (
                    <option value="" disabled>
                      {t.tier ? `${t.tier} (unknown tier)` : '— none —'}
                    </option>
                  )}
                  {tiers.data?.tiers.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.label}
                      {x.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                {changeTier.isError && (
                  <span className="text-xs text-red-600">{(changeTier.error as Error).message}</span>
                )}
                {changeTier.data && !changeTier.data.pushed && (
                  <span
                    className="text-xs text-amber-600"
                    title={changeTier.data.push_reason ?? ''}
                  >
                    saved — not pushed (tenant offline)
                  </span>
                )}
              </div>
            </div>
            <Row label="Customer" value={t.customer_name ?? '—'} />
            <Row label="Company" value={t.company_name ?? '—'} />
            <Row label="Email" value={t.customer_email ?? '—'} />
            <Row label="Legacy hostnames" value={t.legacy_hostnames.join(', ') || '—'} />
            <Row label="Created" value={formatDate(t.created_at)} />
            <Row
              label="Last active"
              value={
                t.last_chat_activity_at
                  ? `${formatDate(t.last_chat_activity_at)} (${relativeTime(t.last_chat_activity_at)})`
                  : '—'
              }
            />
            <div className="flex items-center justify-between gap-4">
              <span className="text-slate-500 text-xs uppercase tracking-wide">Health</span>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Badge tone={containerTone(t.container_status)}>{t.container_status ?? 'unknown'}</Badge>
                {t.health_status ? (
                  <Badge tone={statusTone(t.health_status)}>probe: {t.health_status}</Badge>
                ) : (
                  <span className="text-xs text-slate-400">probe: not run yet</span>
                )}
                {t.last_health_at && (
                  <span className="text-xs text-slate-400">{relativeTime(t.last_health_at)}</span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => recheck.mutate()}
                  disabled={recheck.isPending}
                  title="Re-run health check (HTTP probe + auth reconcile)"
                >
                  <RefreshCw size={14} className={recheck.isPending ? 'animate-spin' : ''} />
                </Button>
              </div>
            </div>
            <Row
              label="Container restarted"
              value={
                t.container_started_at
                  ? `${formatDate(t.container_started_at)} (${relativeTime(t.container_started_at)})`
                  : '—'
              }
            />
            <Row
              label="Claude authorized"
              value={
                t.claude_oauth_authorized_at
                  ? `${formatDate(t.claude_oauth_authorized_at)} (${relativeTime(t.claude_oauth_authorized_at)})`
                  : '—'
              }
            />
            <Row
              label="Claude login ends"
              value={
                t.claude_login_expires_at
                  ? `${formatDate(t.claude_login_expires_at)} (${relativeTime(t.claude_login_expires_at)})`
                  : '—'
              }
            />
            <Row
              label="Codex authorized"
              value={
                t.codex_oauth_authorized_at
                  ? `${formatDate(t.codex_oauth_authorized_at)} (${relativeTime(t.codex_oauth_authorized_at)})`
                  : '—'
              }
            />
            <Row label="Notes" value={t.notes || '—'} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => openDashboard.mutate()}
              disabled={openDashboard.isPending || !isContainerRunning}
              title={
                !isContainerRunning
                  ? `Tenant is ${t.status} — start it before opening the dashboard.`
                  : undefined
              }
            >
              <LayoutGrid size={14} />
              {openDashboard.isPending ? 'Opening…' : 'Open dashboard'}
            </Button>
            {dashboardError && (
              <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">
                {dashboardError}
              </div>
            )}
            {/* Real anchor (not a Button) so the browser handles the
                Content-Disposition: attachment from the tenant directly
                and saves the ZIP without navigating away. Disabled until
                we know the latest version from /plugin/info. */}
            {pluginInfo.data ? (
              <a
                href={pluginInfo.data.download_url}
                className="inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 h-10 px-4 text-sm border border-slate-300 bg-white text-slate-900 hover:bg-slate-50 w-full"
              >
                <Download size={14} />
                Download plugin (v{pluginInfo.data.version})
              </a>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                disabled
                title={
                  !isContainerRunning
                    ? `Tenant is ${t.status} — start it before downloading the plugin.`
                    : pluginInfo.isError
                      ? 'Could not fetch latest plugin version'
                      : 'Loading…'
                }
              >
                <Download size={14} />
                Download plugin
              </Button>
            )}
            {/* License-free build — same plugin, keeps the orchestrator URL but
                omits the bundled license. For handing out a pre-configured but
                unlicensed install. Secondary anchor, same browser-save trick. */}
            {pluginInfo.data?.download_url_no_license && (
              <a
                href={pluginInfo.data.download_url_no_license}
                className="inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 h-8 px-3 text-xs text-slate-700 hover:bg-slate-100 w-full"
              >
                <Download size={12} />
                Download without license
              </a>
            )}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setTokenOpen(true);
                revealToken.mutate();
              }}
            >
              <Eye size={14} />
              Reveal admin token
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setConfirmReseed(true)}
              disabled={!isContainerRunning}
              title={
                !isContainerRunning
                  ? `Tenant is ${t.status} — start it before re-seeding settings.`
                  : 'Re-push the control-plane global default settings to this tenant'
              }
            >
              <Sprout size={14} />
              Re-seed settings
            </Button>
            {isContainerRunning && (
              <Button variant="outline" className="w-full" onClick={() => setConfirmSuspend(true)}>
                <Pause size={14} />
                Suspend
              </Button>
            )}
            {t.status === 'suspended' && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => resume.mutate()}
                disabled={resume.isPending}
                title={
                  t.suspend_kind === 'payment_required'
                    ? 'Clears the payment hold (the container is already running)'
                    : 'Starts the container'
                }
              >
                <Play size={14} />
                Resume
              </Button>
            )}
            <div className="flex items-start gap-2.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
              <Toggle
                checked={t.never_delete}
                onChange={(next) => setNeverDelete.mutate(next)}
                disabled={setNeverDelete.isPending}
                label="Never delete this account"
                className="mt-0.5"
              />
              <div className="text-xs">
                <div className="font-medium text-slate-800">Never delete</div>
                <p className="text-slate-600">
                  {t.never_delete
                    ? 'Locked. No sweep, no mark and no delete can remove this account — including from the external API — until this is turned off.'
                    : 'Lock this account against every removal: sweeps, the grace-period mark, the button below and the external API.'}
                </p>
                {setNeverDelete.isError && (
                  <p className="text-red-700 mt-1">{(setNeverDelete.error as Error).message}</p>
                )}
              </div>
            </div>
            {!t.removal_marked_at && t.status !== 'provisioning' && (
              <Button
                variant="outline"
                className="w-full"
                disabled={t.never_delete}
                onClick={() => {
                  markRemoval.reset();
                  setConfirmMark(true);
                }}
                title={
                  t.never_delete
                    ? 'Never delete is on for this account — turn it off first.'
                    : 'Start the grace period — the account is removed (stop & purge) when it ends'
                }
              >
                <UserX size={14} />
                Mark for removal
              </Button>
            )}
            <Button
              variant="destructive"
              className="w-full"
              disabled={t.never_delete}
              onClick={() => setConfirmDelete(true)}
              title={t.never_delete ? 'Never delete is on for this account — turn it off first.' : undefined}
            >
              <Trash2 size={14} />
              Delete tenant
            </Button>
          </CardBody>
        </Card>
      </div>

      <CredentialsCard
        tenant={t}
        onClaudeAuth={() => setClaudeAuthOpen(true)}
        onCodexAuth={() => setCodexAuthOpen(true)}
        onAnthropicKey={() => setAnthropicKeyOpen(true)}
        onOpenaiKey={() => setOpenaiKeyOpen(true)}
        onClaudeDisconnect={() => setDisconnectTarget('claude')}
        onCodexDisconnect={() => setDisconnectTarget('codex')}
      />

      <TenantSettingsCard tenantId={id} />

      <TenantSkillsCard tenantId={id} running={isContainerRunning} />

      <Card>
        <CardHeader>
          <CardTitle>Activity</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {activity.isLoading ? (
            <div className="p-5 text-sm text-slate-500">Loading…</div>
          ) : !activity.data?.activity.length ? (
            <div className="p-5 text-sm text-slate-500">No activity yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {activity.data.activity.map((entry) => (
                <li key={entry.id} className="px-5 py-2.5 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between text-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge tone={statusTone(entry.outcome)}>{entry.outcome}</Badge>
                    <span className="font-mono text-xs text-slate-600">{entry.action}</span>
                    {entry.error_message && (
                      <span className="text-xs text-red-600 truncate">{entry.error_message}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 whitespace-nowrap">
                    {entry.operator_email ?? 'system'} · {relativeTime(entry.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <OpsEventsCard tenantId={id} />

      <ClaudeAuthDialog open={claudeAuthOpen} onOpenChange={setClaudeAuthOpen} tenantId={id} />
      <CodexAuthDialog open={codexAuthOpen} onOpenChange={setCodexAuthOpen} tenantId={id} />
      <ApiKeyDialog
        open={anthropicKeyOpen}
        onOpenChange={setAnthropicKeyOpen}
        tenantId={id}
        provider="anthropic"
        isSet={t.anthropic_api_key_set}
        setAt={t.anthropic_api_key_set_at}
      />
      <ApiKeyDialog
        open={openaiKeyOpen}
        onOpenChange={setOpenaiKeyOpen}
        tenantId={id}
        provider="openai"
        isSet={t.openai_api_key_set}
        setAt={t.openai_api_key_set_at}
      />

      <Dialog
        open={disconnectTarget !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDisconnectTarget(null);
            disconnect.reset();
          }
        }}
        title={<>Disconnect {disconnectTarget === 'codex' ? 'Codex' : 'Claude'} subscription</>}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setDisconnectTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={disconnect.isPending}
              onClick={() => {
                if (disconnectTarget) disconnect.mutate(disconnectTarget);
              }}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Signs the customer out and clears the stored{' '}
          {disconnectTarget === 'codex' ? 'Codex' : 'Claude'} subscription on this tenant. Chats
          relying on subscription auth will stop working until it's reconnected. API keys are
          unaffected.
        </p>
        {disconnect.isError && (
          <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2 mt-3">
            {disconnect.error.message}
          </div>
        )}
      </Dialog>

      <Dialog
        open={confirmReseed}
        onOpenChange={(o) => {
          setConfirmReseed(o);
          if (!o) reseed.reset();
        }}
        title="Re-seed settings"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmReseed(false)}>
              Cancel
            </Button>
            <Button disabled={reseed.isPending} onClick={() => reseed.mutate()}>
              {reseed.isPending ? 'Re-seeding…' : 'Re-seed'}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <p className="text-sm text-slate-700">
            Re-pushes the control-plane global defaults (modes, default mode, model
            switch, credit / version / compaction policy) onto{' '}
            <span className="font-mono text-slate-900">{t.id}</span> in a single write —
            the same seed that runs at tenant creation.
          </p>
          <p className="text-sm text-slate-700">
            Use this to repair a tenant whose creation-time seed failed (no{' '}
            <code className="font-mono text-xs">settings.json</code>). Note it{' '}
            <b>overwrites any settings the tenant changed in their own dashboard</b> with
            the global values.
          </p>
          {reseed.isError && (
            <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2 mt-1">
              {(reseed.error as Error).message}
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        open={confirmSuspend}
        onOpenChange={(o) => {
          setConfirmSuspend(o);
          if (!o) {
            setSuspendKind('hard');
            suspend.reset();
          }
        }}
        title={<>Suspend tenant <span className="font-mono text-slate-600">{t.id}</span></>}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmSuspend(false)}>
              Cancel
            </Button>
            <Button disabled={suspend.isPending} onClick={() => suspend.mutate(suspendKind)}>
              {suspend.isPending ? 'Suspending…' : 'Suspend'}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <DeleteModeOption
            name="suspend-kind"
            value="hard"
            checked={suspendKind === 'hard'}
            onChange={setSuspendKind}
            title="Hard — stop the container"
            body={
              <>
                <code className="font-mono text-[11px]">docker stop</code> on the host. Everything
                is down until resumed: chats, tenant dashboard, admin endpoints. Data preserved.
              </>
            }
          />
          <DeleteModeOption
            name="suspend-kind"
            value="payment_required"
            checked={suspendKind === 'payment_required'}
            onChange={setSuspendKind}
            title="Payment hold — block chatting only"
            body={
              <>
                Container keeps running. Every chat send is answered with a "problem with your
                subscription" message and the plugin shows a red payment bar above the prompt. This
                is what the billing system's automatic suspend uses. Resume clears it.
              </>
            }
          />
          {suspend.isError && (
            <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2 mt-3">
              {(suspend.error as Error).message}
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        open={tokenOpen}
        onOpenChange={(o) => {
          setTokenOpen(o);
          if (!o) revealToken.reset();
        }}
        title="Admin token"
        size="lg"
        footer={
          <Button variant="outline" onClick={() => setTokenOpen(false)}>
            Close
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-700">
            The tenant's{' '}
            <code className="font-mono text-xs">ORCHESTRATOR_ADMIN_TOKEN</code>. Used by the
            control plane as the <code className="font-mono text-xs">X-Admin-Token</code> header
            when calling tenant endpoints, and recorded on the host in{' '}
            <code className="font-mono text-xs">/opt/godmode/tenants/{id}.env</code>. Treat as a
            secret.
          </p>
          {revealToken.isPending && <div className="text-sm text-slate-500">Loading…</div>}
          {revealToken.isError && (
            <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">
              {revealToken.error.message}
            </div>
          )}
          {revealToken.data && (
            <div className="flex gap-2">
              <Input value={revealToken.data.admin_token} readOnly className="font-mono text-xs" />
              <Button
                variant="outline"
                onClick={() => navigator.clipboard.writeText(revealToken.data!.admin_token)}
              >
                <Copy size={14} /> Copy
              </Button>
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        open={confirmDelete}
        onOpenChange={(o) => {
          setConfirmDelete(o);
          if (!o) {
            setDeleteMode('stop');
            setDeleteError(null);
          }
        }}
        title={<>Delete tenant <span className="font-mono text-slate-600">{t.id}</span></>}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => {
                setDeleteError(null);
                remove.mutate(deleteMode, {
                  onError: (err: Error) => setDeleteError(err.message),
                });
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <DeleteModeOption
            value="stop"
            checked={deleteMode === 'stop'}
            onChange={setDeleteMode}
            title="Stop container"
            body={
              <>
                SSH to the host and run <code className="font-mono text-[11px]">remove-tenant.sh</code>.
                Data and env file kept on disk. Tenant row soft-deleted.
              </>
            }
          />
          <DeleteModeOption
            value="stop_purge"
            checked={deleteMode === 'stop_purge'}
            onChange={setDeleteMode}
            title="Stop & purge data"
            body={
              <>
                Adds <code className="font-mono text-[11px]">--purge-data</code> — wipes{' '}
                <code className="font-mono text-[11px]">/var/godmode/tenants/{t.id}/</code>,{' '}
                <code className="font-mono text-[11px]">/opt/godmode/tenants/{t.id}.env</code> and its
                backups. <b className="text-red-700">Unrecoverable.</b>
              </>
            }
          />
          <DeleteModeOption
            value="cp_only"
            checked={deleteMode === 'cp_only'}
            onChange={setDeleteMode}
            title="CP record only (no SSH)"
            body={
              <>
                Hard-deletes the row in the control plane. Container and host files stay.
                Use when SSH or admin-token decryption is broken (e.g. lost{' '}
                <code className="font-mono text-[11px]">CP_MASTER_KEY</code>) or the container
                is already gone. Frees the tenant id for immediate re-provisioning.
              </>
            }
          />
          <p className="text-xs text-slate-600 pt-1">
            {sweeper.data?.config.removed_webhook_url ? (
              <>
                Your external system will be notified (
                <code className="font-mono text-[11px]">tenant.removed</code> webhook).{' '}
              </>
            ) : null}
            The deletion is recorded in the removal log on the Cleanup page.
          </p>
          {deleteError && (
            <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2 mt-3">
              {deleteError}
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        open={confirmMark}
        onOpenChange={(o) => !markRemoval.isPending && setConfirmMark(o)}
        title={<>Mark <span className="font-mono text-slate-600">{t.id}</span> for removal</>}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmMark(false)} disabled={markRemoval.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={markRemoval.isPending} onClick={() => markRemoval.mutate()}>
              {markRemoval.isPending ? 'Marking…' : 'Mark for removal'}
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm text-slate-700">
          <p>
            Starts a <b>{graceDays}-day</b> grace period. When it ends, the automatic sweeper stops the
            container and <b>purges all its data</b> — even if the account is used again in the meantime.
            Postpone or unmark to stop it.
          </p>
          <p>
            Your external system gets a{' '}
            <code className="font-mono text-xs">tenant.removal_marked</code> webhook now
            {sweeper.data && !sweeper.data.config.marked_webhook_url ? ' (no URL set on the Cleanup page)' : ''}.
          </p>
          {sweeper.data && !sweeperOn && (
            <div className="text-xs text-amber-800 bg-amber-50 rounded-md px-3 py-2">
              The automatic sweeper is off, so the account is not removed until you turn it on on the
              Cleanup page.
            </div>
          )}
          {markRemoval.isError && (
            <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">
              {(markRemoval.error as Error).message}
            </div>
          )}
        </div>
      </Dialog>

      <PostponeDialog
        tenantId={postponeOpen ? t.id : null}
        dueAt={t.removal_due_at}
        days={postponeDays}
        onDays={setPostponeDays}
        pending={postponeRemoval.isPending}
        error={postponeRemoval.isError ? (postponeRemoval.error as Error).message : null}
        onCancel={() => setPostponeOpen(false)}
        onConfirm={() => postponeRemoval.mutate(postponeDays)}
      />

      {dashboardEmbedUrl &&
        createPortal(
          // Portal into <body> so the overlay sits in the viewport's containing
          // block, not whatever ancestor of TenantDetail happens to clip/transform
          // the layout. On desktop, left-56 lines up with the persistent CP
          // sidebar (lg:w-56) so its links stay clickable; on mobile the sidebar
          // is an off-canvas drawer, so the overlay goes edge-to-edge (left-0).
          <div className="fixed top-0 right-0 bottom-0 left-0 lg:left-56 z-50 flex flex-col bg-white border-l border-slate-200 shadow-2xl">
            <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-slate-200">
              <div className="flex items-center gap-2 text-sm">
                <LayoutGrid size={14} className="text-slate-500" />
                <span className="font-mono text-slate-700">{t.id}</span>
                <span className="text-slate-400">dashboard</span>
              </div>
              <button
                type="button"
                onClick={() => setDashboardEmbedUrl(null)}
                className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded hover:bg-slate-100"
                aria-label="Close dashboard"
              >
                Close <X size={14} />
              </button>
            </div>
            <iframe
              src={dashboardEmbedUrl}
              title={`${t.id} dashboard`}
              className="flex-1 w-full bg-white border-0"
            />
          </div>,
          document.body
        )}
    </div>
  );
}

function TenantSettingsCard({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const fields = useQuery({
    queryKey: ['tenant-settings', tenantId],
    queryFn: () =>
      api.get<{ fields: TenantSettingField[]; live_ok: boolean }>(
        `/api/tenants/${tenantId}/settings`
      ),
  });

  const overrideCount = fields.data?.fields.filter((f) => f.source === 'override').length ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Settings</CardTitle>
          {overrideCount > 0 && (
            <Badge tone="info">
              {overrideCount} {overrideCount === 1 ? 'override' : 'overrides'}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {fields.isLoading ? (
          <div className="p-5 text-sm text-slate-500">Loading…</div>
        ) : !fields.data?.fields.length ? (
          <div className="p-5 text-sm text-slate-500">No fields.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Field</th>
                <th className="px-3 py-3">Effective value</th>
                <th className="px-3 py-3">Source</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {fields.data.fields.map((f) => (
                <SettingsRow
                  key={f.key}
                  tenantId={tenantId}
                  field={f}
                  liveOk={fields.data.live_ok}
                  onChanged={() => qc.invalidateQueries({ queryKey: ['tenant-settings', tenantId] })}
                />
              ))}
            </tbody>
          </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function SettingsRow({
  tenantId,
  field,
  liveOk,
  onChanged,
}: {
  tenantId: string;
  field: TenantSettingField;
  liveOk: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(() =>
    formatForEditor(field.effective ?? field.cp_global, field.type)
  );
  const [error, setError] = useState<string | null>(null);

  const put = useMutation({
    mutationFn: async () => {
      const parsed = parseFromEditor(draft, field.type);
      return api.put<{ ok: boolean; propagation: { pushed_to_orchestrator: boolean; reason: string | null } }>(
        `/api/tenants/${tenantId}/settings/${encodeURIComponent(field.key)}`,
        { value: parsed }
      );
    },
    onSuccess: () => {
      setEditing(false);
      setError(null);
      onChanged();
    },
    onError: (err: Error) => setError(err.message),
  });

  const clear = useMutation({
    mutationFn: () =>
      api.del(`/api/tenants/${tenantId}/settings/${encodeURIComponent(field.key)}`),
    onSuccess: () => onChanged(),
  });

  if (field.kind === 'seed' || field.kind === 'locked') {
    return (
      <tr className="border-b border-slate-100 last:border-0">
        <td className="px-5 py-3">
          <div className="font-mono text-xs text-slate-900">{field.key}</div>
          <div className="text-xs text-slate-500">{field.label}</div>
        </td>
        <td className="px-3 py-3 text-xs font-mono text-slate-700">
          {formatForDisplay(field.cp_global, field.type)}
        </td>
        <td className="px-3 py-3">
          <Badge tone="neutral">
            {field.kind === 'seed' ? 'seed only (set at creation)' : 'locked (managed globally)'}
          </Badge>
        </td>
        <td />
      </tr>
    );
  }

  // Shown, but not overridable from the CP — tenants manage these in their own
  // dashboard. Modes render as a friendly summary rather than raw JSON.
  if (!field.tenant_overridable) {
    return (
      <tr className="border-b border-slate-100 last:border-0 align-top">
        <td className="px-5 py-3">
          <div className="font-mono text-xs text-slate-900">{field.key}</div>
          <div className="text-xs text-slate-500">{field.label}</div>
        </td>
        <td className="px-3 py-3">
          <SettingValueDisplay field={field} />
        </td>
        <td className="px-3 py-3">
          {!liveOk ? (
            <span title="Couldn't read the tenant's live settings — showing the CP global default instead.">
              <Badge tone="warning">live unavailable</Badge>
            </span>
          ) : field.source === 'tenant' ? (
            <span title="This tenant's live value differs from what the control plane last pushed to it — changed in the tenant's own dashboard. A normal 'Push to tenants' skips it; force-push to overwrite.">
              <Badge tone="info">customized</Badge>
            </span>
          ) : (
            <span title="This tenant's live value matches what the control plane last pushed to it.">
              <Badge tone="neutral">unchanged</Badge>
            </span>
          )}
        </td>
        <td className="px-3 py-3 text-right">
          <span
            className="text-xs text-slate-400 whitespace-nowrap"
            title="Pushed from global settings. Tenants change this in their own Godmode dashboard — it can't be overridden from the control plane."
          >
            not overridable here
          </span>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-slate-100 last:border-0 align-top">
      <td className="px-5 py-3">
        <div className="font-mono text-xs text-slate-900">{field.key}</div>
        <div className="text-xs text-slate-500">{field.label}</div>
      </td>
      <td className="px-3 py-3">
        {editing ? (
          <div className="space-y-1">
            {field.type === 'boolean' ? (
              <select
                className="block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              >
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : field.type === 'json' ? (
              <textarea
                rows={6}
                className="block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-mono"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            ) : (
              <input
                className="block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            )}
            {error && <div className="text-xs text-red-600">{error}</div>}
          </div>
        ) : (
          <div className="text-xs font-mono text-slate-700 whitespace-pre-wrap break-all">
            {formatForDisplay(field.effective, field.type)}
          </div>
        )}
      </td>
      <td className="px-3 py-3">
        <Badge tone={field.source === 'override' ? 'info' : 'neutral'}>
          {field.source === 'override' ? 'tenant override' : 'inherited from global'}
        </Badge>
      </td>
      <td className="px-3 py-3 text-right whitespace-nowrap">
        {editing ? (
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setEditing(false); setError(null); }}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => put.mutate()} disabled={put.isPending}>
              {put.isPending ? 'Saving…' : 'Save override'}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => {
              setDraft(formatForEditor(field.effective, field.type));
              setEditing(true);
            }}>
              {field.source === 'override' ? 'Edit' : 'Override'}
            </Button>
            {field.source === 'override' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => clear.mutate()}
                title="Revert to CP-global value"
              >
                Clear
              </Button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function formatForEditor(value: unknown, type: TenantSettingField['type']): string {
  if (type === 'json') return JSON.stringify(value ?? null, null, 2);
  if (type === 'boolean') return value === true ? 'true' : 'false';
  if (value == null) return '';
  return String(value);
}
function parseFromEditor(raw: string, type: TenantSettingField['type']): unknown {
  if (type === 'json') return JSON.parse(raw);
  if (type === 'boolean') return raw === 'true';
  if (type === 'number') {
    const n = Number(raw);
    if (Number.isNaN(n)) throw new Error('Not a valid number');
    return n;
  }
  return raw;
}
function formatForDisplay(value: unknown, type: TenantSettingField['type']): string {
  if (type === 'json') return JSON.stringify(value, null, 2);
  if (type === 'boolean') return String(value === true);
  if (value == null) return '—';
  return String(value);
}

// Read-only value renderer for the settings table. Modes get a friendly
// per-mode summary; everything else falls back to the plain text display.
function SettingValueDisplay({ field }: { field: TenantSettingField }) {
  if (field.key === 'modes' && Array.isArray(field.effective)) {
    return <ModesSummary modes={field.effective as Mode[]} />;
  }
  return (
    <div className="text-xs font-mono text-slate-700 whitespace-pre-wrap break-all">
      {formatForDisplay(field.effective, field.type)}
    </div>
  );
}

// One line on why a tenant is marked for removal, from its removal_context.
function removalReason(t: Tenant): string {
  const c = t.removal_context;
  if (!c) return '';
  const who =
    c.rule === 'manual'
      ? `Marked by hand${c.operator_email ? ` by ${c.operator_email}` : ''}`
      : `Matched sweep rule "${c.rule}"`;
  const facts = [
    c.last_used_at ? `last used ${relativeTime(c.last_used_at)}` : null,
    c.site_count !== null ? `${c.site_count} site${c.site_count === 1 ? '' : 's'}` : null,
    c.ai_authenticated === false ? 'no AI authenticated' : null,
  ].filter(Boolean);
  return `${who}${facts.length ? ` (${facts.join(', ')})` : ''}.`;
}

// statusTone doesn't know the container vocabulary (running/stopped/missing),
// so map it here: running = healthy green, stopped = amber, missing = red.
function containerTone(
  status: Tenant['container_status']
): 'neutral' | 'success' | 'warning' | 'danger' {
  return status === 'running'
    ? 'success'
    : status === 'stopped'
    ? 'warning'
    : status === 'missing'
    ? 'danger'
    : 'neutral';
}

function Row({ label, value, link }: { label: string; value: string; link?: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500 text-xs uppercase tracking-wide">{label}</span>
      {link ? (
        <Link to={link} className="text-slate-900 font-mono text-xs hover:underline break-all text-right">
          {value}
        </Link>
      ) : (
        <span className="text-slate-900 font-mono text-xs break-all text-right">{value}</span>
      )}
    </div>
  );
}

interface InitiateResult {
  auth_url: string;
  sse_url: string;
}

interface CodexInitiateResult extends InitiateResult {
  device_code: string;
}

function ClaudeAuthDialog({
  open,
  onOpenChange,
  tenantId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  tenantId: string;
}) {
  const qc = useQueryClient();
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [sseUrl, setSseUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'initiating' | 'awaiting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  // Re-authorization tracking. `reauth` = this flow started on an already-
  // authorized tenant, so the canonical check reports the OLD (still-valid)
  // credentials as authorized from the start and `authorized: true` alone can't
  // mean "done". `reauthAtBaseline` is the tenant's authorized_at captured when
  // the flow began; completion is detected when it advances past this (the
  // proxy bumps it to now() on success even if the browser missed the SSE).
  const [reauth, setReauth] = useState(false);
  const [reauthAtBaseline, setReauthAtBaseline] = useState<string | null>(null);
  const sseRef = useRef<EventSource | null>(null);

  // Canonical poll — `claude auth status --json` on the tenant container.
  // Runs at 2s cadence while the dialog is open. Catches:
  //   - Tenant was already authorized before the dialog opened
  //   - User finished the OAuth handshake elsewhere mid-session
  //   - SSE proxy dropped a 'success' event for any reason
  // Same queryKey as the page-level reconciliation; React Query de-dupes them.
  const authStatus = useQuery({
    queryKey: ['tenant-claude-auth-status', tenantId],
    queryFn: async () => {
      const data = await api.get<{
        authorized: boolean;
        authorized_at: string | null;
        tenant: Tenant;
      }>(`/api/tenants/${tenantId}/claude-auth-status`);
      qc.setQueryData<{ tenant: Tenant }>(['tenant', tenantId], { tenant: data.tenant });
      return data;
    },
    enabled: open,
    refetchInterval: open && status !== 'success' && status !== 'error' ? 2000 : false,
  });

  useEffect(() => {
    if (!authStatus.data?.authorized) return;
    if (status === 'success') return;
    // Don't short-circuit while the user is still on the idle screen — they
    // may want to re-authorize. Only auto-flip when an active flow is in
    // progress (initiating/awaiting/error) and the canonical check
    // independently observes authorized.
    if (status === 'idle') return;
    // Re-authorization: the still-valid old credentials keep the canonical
    // check reporting `authorized` from the instant the URL is generated, so
    // `authorized: true` alone would jump straight to "success" and hide the
    // rest of the flow (incl. the paste-code fallback). Instead wait for the
    // tenant's authorized_at to advance past the value captured when the flow
    // began — the proxy's success handler bumps it to now() even when the
    // browser misses the terminal SSE event, so this also recovers a dialog
    // that would otherwise sit on "Waiting" after a dropped event. Read it off
    // the tenant row: the top-level authorized_at is null (the orchestrator
    // check returns no timestamp).
    if (reauth) {
      const at = authStatus.data?.tenant?.claude_oauth_authorized_at ?? null;
      if (at === null || at === reauthAtBaseline) return;
    }
    setStatus('success');
    qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
    qc.invalidateQueries({ queryKey: ['tenants'] });
    sseRef.current?.close();
  }, [
    authStatus.data?.authorized,
    authStatus.data?.tenant?.claude_oauth_authorized_at,
    status,
    reauth,
    reauthAtBaseline,
    qc,
    tenantId,
  ]);

  const initiate = useMutation({
    mutationFn: () => api.post<InitiateResult>(`/api/tenants/${tenantId}/claude-setup-token`),
    onMutate: () => {
      // Capture whether we're re-authorizing, and the baseline authorized_at,
      // before the new flow muddies the canonical check (see `reauth` above).
      const wasAuthorized = authStatus.data?.authorized === true;
      setReauth(wasAuthorized);
      setReauthAtBaseline(
        wasAuthorized ? authStatus.data?.tenant?.claude_oauth_authorized_at ?? null : null
      );
      setStatus('initiating');
      setError(null);
    },
    onSuccess: (data) => {
      setAuthUrl(data.auth_url);
      setSseUrl(data.sse_url);
      setStatus('awaiting');
    },
    onError: (err: Error) => {
      setStatus('error');
      setError(err.message);
    },
  });

  const submitCode = useMutation({
    mutationFn: () => api.post(`/api/tenants/${tenantId}/claude-setup-token/submit-code`, { code }),
    onError: (err: Error) => setError(err.message),
  });

  useEffect(() => {
    if (!sseUrl) return;
    const es = new EventSource(sseUrl, { withCredentials: true });
    sseRef.current = es;
    es.addEventListener('state', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent).data);
        if (payload.status === 'success') {
          setStatus('success');
          qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
          qc.invalidateQueries({ queryKey: ['tenants'] });
          es.close();
        } else if (payload.status === 'error' || payload.status === 'timeout') {
          setStatus('error');
          setError(payload.error || payload.status);
          es.close();
        }
      } catch {
        /* ignore */
      }
    });
    es.addEventListener('error', () => {
      // SSE auto-reconnects; only surface as failure once.
      if (status === 'awaiting') {
        // give the upstream a moment to recover
      }
    });
    return () => {
      es.close();
      sseRef.current = null;
    };
  }, [sseUrl, tenantId, qc, status]);

  useEffect(() => {
    if (!open) {
      setAuthUrl(null);
      setSseUrl(null);
      setStatus('idle');
      setError(null);
      setCode('');
      setReauth(false);
      setReauthAtBaseline(null);
      sseRef.current?.close();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Connect Claude subscription"
      size="lg"
      footer={
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        {status === 'idle' && authStatus.isLoading && (
          <div className="text-sm text-slate-500">Checking authorization status…</div>
        )}
        {status === 'idle' && !authStatus.isLoading && (
          <div className="space-y-3">
            {authStatus.data?.authorized && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <Badge tone="success">Currently authorized</Badge>
                <p className="mt-1.5 text-xs text-emerald-800">
                  Re-authorizing replaces the existing credentials on this tenant — the customer
                  signs in again, fresh tokens are written. The previous credentials stop working.
                </p>
              </div>
            )}
            <p className="text-sm text-slate-700">
              Generates a one-time authorization URL on the tenant's container. Send the URL to the
              customer; they'll complete sign-in in their browser and this dialog will update
              automatically.
            </p>
            <Button onClick={() => initiate.mutate()}>
              {authStatus.data?.authorized ? 'Generate new authorization URL' : 'Generate authorization URL'}
            </Button>
          </div>
        )}
        {status === 'initiating' && <div className="text-sm text-slate-500">Initiating session…</div>}
        {(status === 'awaiting' || status === 'error' || status === 'success') && authUrl && (
          <div className="space-y-3">
            <div>
              <Label>Authorization URL</Label>
              <div className="flex gap-2">
                <Input value={authUrl} readOnly />
                <Button
                  variant="outline"
                  onClick={() => navigator.clipboard.writeText(authUrl)}
                >
                  <Copy size={14} /> Copy
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Deliver this URL to the customer (email, chat, etc.). It works only with their
                browser.
              </p>
            </div>

            <div className="text-sm">
              <span className="text-slate-500">Status:&nbsp;</span>
              {status === 'awaiting' && <Badge tone="warning">Waiting for customer</Badge>}
              {status === 'success' && <Badge tone="success">Authorized</Badge>}
              {status === 'error' && <Badge tone="danger">{error || 'Failed'}</Badge>}
            </div>

            {status === 'awaiting' && (
              <details className="border border-slate-200 rounded-md">
                <summary className="px-3 py-2 text-xs text-slate-700 cursor-pointer">
                  Fallback: paste authorization code
                </summary>
                <div className="p-3 space-y-2 border-t border-slate-200">
                  <p className="text-xs text-slate-500">
                    Only needed if the customer's browser shows them a code instead of finishing
                    automatically.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Code from customer"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                    <Button
                      variant="outline"
                      onClick={() => submitCode.mutate()}
                      disabled={!code || submitCode.isPending}
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}

function CodexAuthDialog({
  open,
  onOpenChange,
  tenantId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  tenantId: string;
}) {
  const qc = useQueryClient();
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [deviceCode, setDeviceCode] = useState<string | null>(null);
  const [sseUrl, setSseUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'initiating' | 'awaiting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  // Re-auth tracking — see the matching note in ClaudeAuthDialog. `reauth` =
  // started on an already-authorized tenant; `reauthAtBaseline` = the tenant's
  // authorized_at at flow start, used to detect completion when it advances.
  const [reauth, setReauth] = useState(false);
  const [reauthAtBaseline, setReauthAtBaseline] = useState<string | null>(null);
  const sseRef = useRef<EventSource | null>(null);

  // Canonical poll — `codex login status` on the tenant container.
  const authStatus = useQuery({
    queryKey: ['tenant-codex-auth-status', tenantId],
    queryFn: async () => {
      const data = await api.get<{
        authorized: boolean;
        authorized_at: string | null;
        tenant: Tenant;
      }>(`/api/tenants/${tenantId}/codex-auth-status`);
      qc.setQueryData<{ tenant: Tenant }>(['tenant', tenantId], { tenant: data.tenant });
      return data;
    },
    enabled: open,
    refetchInterval: open && status !== 'success' && status !== 'error' ? 2000 : false,
  });

  useEffect(() => {
    if (!authStatus.data?.authorized) return;
    if (status === 'success') return;
    // Don't short-circuit while the user is still on the idle screen — they
    // may want to re-authorize. Only auto-flip when an active flow is in
    // progress and the canonical check independently observes authorized.
    if (status === 'idle') return;
    // Re-authorization: the still-valid old credentials report `authorized`
    // from the instant the URL is generated, so wait for the tenant's
    // authorized_at to advance past the flow-start baseline instead of flipping
    // immediately. The proxy bumps it on success even if the browser misses the
    // SSE event, so this also recovers a dialog left on "Waiting" after a drop.
    // Read it off the tenant row (top-level authorized_at is null).
    if (reauth) {
      const at = authStatus.data?.tenant?.codex_oauth_authorized_at ?? null;
      if (at === null || at === reauthAtBaseline) return;
    }
    setStatus('success');
    qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
    qc.invalidateQueries({ queryKey: ['tenants'] });
    sseRef.current?.close();
  }, [
    authStatus.data?.authorized,
    authStatus.data?.tenant?.codex_oauth_authorized_at,
    status,
    reauth,
    reauthAtBaseline,
    qc,
    tenantId,
  ]);

  const initiate = useMutation({
    mutationFn: () => api.post<CodexInitiateResult>(`/api/tenants/${tenantId}/codex-setup-token`),
    onMutate: () => {
      const wasAuthorized = authStatus.data?.authorized === true;
      setReauth(wasAuthorized);
      setReauthAtBaseline(
        wasAuthorized ? authStatus.data?.tenant?.codex_oauth_authorized_at ?? null : null
      );
      setStatus('initiating');
      setError(null);
    },
    onSuccess: (data) => {
      setAuthUrl(data.auth_url);
      setDeviceCode(data.device_code);
      setSseUrl(data.sse_url);
      setStatus('awaiting');
    },
    onError: (err: Error) => {
      setStatus('error');
      setError(err.message);
    },
  });

  useEffect(() => {
    if (!sseUrl) return;
    const es = new EventSource(sseUrl, { withCredentials: true });
    sseRef.current = es;
    es.addEventListener('state', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent).data);
        if (payload.status === 'success') {
          setStatus('success');
          qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
          qc.invalidateQueries({ queryKey: ['tenants'] });
          es.close();
        } else if (payload.status === 'error' || payload.status === 'timeout') {
          setStatus('error');
          setError(payload.error || payload.status);
          es.close();
        }
      } catch {
        /* ignore */
      }
    });
    return () => {
      es.close();
      sseRef.current = null;
    };
  }, [sseUrl, tenantId, qc]);

  useEffect(() => {
    if (!open) {
      setAuthUrl(null);
      setDeviceCode(null);
      setSseUrl(null);
      setStatus('idle');
      setError(null);
      setReauth(false);
      setReauthAtBaseline(null);
      sseRef.current?.close();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Connect Codex subscription"
      size="lg"
      footer={
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        {status === 'idle' && authStatus.isLoading && (
          <div className="text-sm text-slate-500">Checking authorization status…</div>
        )}
        {status === 'idle' && !authStatus.isLoading && (
          <div className="space-y-3">
            {authStatus.data?.authorized && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <Badge tone="success">Currently authorized</Badge>
                <p className="mt-1.5 text-xs text-emerald-800">
                  Re-authorizing replaces the existing credentials on this tenant — the customer
                  signs in again, fresh tokens are written. The previous credentials stop working.
                </p>
              </div>
            )}
            <p className="text-sm text-slate-700">
              Generates a device-auth URL and a one-time device code on the tenant's container.
              Send <strong>both</strong> to the customer — they'll open the URL, enter the device
              code, and this dialog will update automatically.
            </p>
            <Button onClick={() => initiate.mutate()}>
              {authStatus.data?.authorized ? 'Generate new authorization URL' : 'Generate authorization URL'}
            </Button>
          </div>
        )}
        {status === 'initiating' && <div className="text-sm text-slate-500">Initiating session…</div>}
        {(status === 'awaiting' || status === 'error' || status === 'success') && authUrl && (
          <div className="space-y-3">
            <div>
              <Label>Authorization URL</Label>
              <div className="flex gap-2">
                <Input value={authUrl} readOnly />
                <Button variant="outline" onClick={() => navigator.clipboard.writeText(authUrl)}>
                  <Copy size={14} /> Copy
                </Button>
              </div>
            </div>
            {deviceCode && (
              <div>
                <Label>Device code</Label>
                <div className="flex gap-2">
                  <Input value={deviceCode} readOnly className="font-mono tracking-widest text-lg" />
                  <Button variant="outline" onClick={() => navigator.clipboard.writeText(deviceCode)}>
                    <Copy size={14} /> Copy
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Customer enters this code on the authorization page. Expires in 15 minutes.
                </p>
              </div>
            )}
            <div className="text-sm">
              <span className="text-slate-500">Status:&nbsp;</span>
              {status === 'awaiting' && <Badge tone="warning">Waiting for customer</Badge>}
              {status === 'success' && <Badge tone="success">Authorized</Badge>}
              {status === 'error' && <Badge tone="danger">{error || 'Failed'}</Badge>}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

function ApiKeyDialog({
  open,
  onOpenChange,
  tenantId,
  provider,
  isSet,
  setAt,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  tenantId: string;
  provider: 'anthropic' | 'openai';
  isSet: boolean;
  setAt: string | null;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fieldName = provider === 'anthropic' ? 'anthropic_api_key' : 'openai_api_key';
  const envName = provider === 'anthropic' ? 'ANTHROPIC_TENANT_API_KEY' : 'OPENAI_TENANT_API_KEY';
  const title = provider === 'anthropic' ? 'Claude API key' : 'Codex API key';

  const save = useMutation({
    mutationFn: (body: Record<string, string | null>) =>
      api.patch(`/api/tenants/${tenantId}/api-keys`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      setValue('');
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  useEffect(() => {
    if (!open) {
      setValue('');
      setError(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="lg"
      footer={
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-700">
          Stored encrypted in the control plane and pushed to the tenant container as{' '}
          <code className="font-mono text-xs">{envName}</code>. Takes effect immediately — the
          orchestrator picks it up without a restart. Alternative to subscription OAuth.
        </p>
        <div>
          <div className="text-sm mb-2">
            <span className="text-slate-500">Current:&nbsp;</span>
            {isSet ? (
              <Badge tone="success">
                Configured{setAt ? ` (${relativeTime(setAt)})` : ''}
              </Badge>
            ) : (
              <Badge tone="neutral">Not set</Badge>
            )}
          </div>
          <Label>{title}</Label>
          <Input
            type="password"
            placeholder={provider === 'anthropic' ? 'sk-ant-…' : 'sk-…'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
          />
        </div>
        {error && (
          <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
        )}
        <div className="flex gap-2">
          <Button
            onClick={() => save.mutate({ [fieldName]: value.trim() })}
            disabled={!value.trim() || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save key'}
          </Button>
          {isSet && (
            <Button
              variant="outline"
              onClick={() => save.mutate({ [fieldName]: null })}
              disabled={save.isPending}
            >
              Clear key
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

function CredentialsCard({
  tenant,
  onClaudeAuth,
  onCodexAuth,
  onAnthropicKey,
  onOpenaiKey,
  onClaudeDisconnect,
  onCodexDisconnect,
}: {
  tenant: Tenant;
  onClaudeAuth: () => void;
  onCodexAuth: () => void;
  onAnthropicKey: () => void;
  onOpenaiKey: () => void;
  onClaudeDisconnect: () => void;
  onCodexDisconnect: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Credentials</CardTitle>
      </CardHeader>
      <CardBody className="space-y-1">
        <CredentialRow
          name="Claude subscription"
          detail={subscriptionDetail(tenant.claude_oauth_email, tenant.claude_oauth_plan)}
          status={
            tenant.claude_oauth_status === 'authorized' ? (
              <Badge tone="success">authorized</Badge>
            ) : (
              <Badge tone={statusTone(tenant.claude_oauth_status)}>{tenant.claude_oauth_status}</Badge>
            )
          }
          setAt={tenant.claude_oauth_authorized_at}
          actionLabel={tenant.claude_oauth_status === 'authorized' ? 'Re-authorize' : 'Connect'}
          onAction={onClaudeAuth}
          onDisconnect={onClaudeDisconnect}
          canDisconnect={
            tenant.claude_oauth_status === 'authorized' || tenant.claude_oauth_status === 'expired'
          }
        />
        <CredentialRow
          name="Claude API key"
          status={
            tenant.anthropic_api_key_set ? (
              <Badge tone="success">configured</Badge>
            ) : (
              <Badge tone="neutral">not set</Badge>
            )
          }
          setAt={tenant.anthropic_api_key_set_at}
          actionLabel={tenant.anthropic_api_key_set ? 'Manage' : 'Set key'}
          onAction={onAnthropicKey}
        />
        <CredentialRow
          name="Codex subscription"
          detail={subscriptionDetail(tenant.codex_oauth_email, tenant.codex_oauth_plan)}
          status={
            tenant.codex_oauth_status === 'authorized' ? (
              <Badge tone="success">authorized</Badge>
            ) : (
              <Badge tone={statusTone(tenant.codex_oauth_status)}>{tenant.codex_oauth_status}</Badge>
            )
          }
          setAt={tenant.codex_oauth_authorized_at}
          actionLabel={tenant.codex_oauth_status === 'authorized' ? 'Re-authorize' : 'Connect'}
          onAction={onCodexAuth}
          onDisconnect={onCodexDisconnect}
          canDisconnect={
            tenant.codex_oauth_status === 'authorized' || tenant.codex_oauth_status === 'expired'
          }
        />
        <CredentialRow
          name="Codex API key"
          status={
            tenant.openai_api_key_set ? (
              <Badge tone="success">configured</Badge>
            ) : (
              <Badge tone="neutral">not set</Badge>
            )
          }
          setAt={tenant.openai_api_key_set_at}
          actionLabel={tenant.openai_api_key_set ? 'Manage' : 'Set key'}
          onAction={onOpenaiKey}
        />
        <p className="text-xs text-slate-500 pt-2">
          Tenant chats work when <strong>any</strong> of the four slots is configured. Subscription
          OAuth uses the customer's plan; API keys bill the deployment owner directly.
        </p>
      </CardBody>
    </Card>
  );
}

// Compact "whose subscription + what plan" line for the OAuth credential rows.
// Raw values as the provider reports them; null when API-key / unauthorized.
function subscriptionDetail(email: string | null, plan: string | null): ReactNode {
  if (!email && !plan) return null;
  return (
    <>
      {email ?? 'unknown account'}
      {plan ? ` · ${plan}` : ''}
    </>
  );
}

function CredentialRow({
  name,
  detail,
  status,
  setAt,
  actionLabel,
  onAction,
  onDisconnect,
  canDisconnect,
}: {
  name: string;
  detail?: ReactNode;
  status: ReactNode;
  setAt: string | null;
  actionLabel: string;
  onAction: () => void;
  onDisconnect?: () => void;
  canDisconnect?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3 min-w-0">
        <KeyRound size={14} className="text-slate-400 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900">{name}</div>
          {detail && <div className="text-xs text-slate-600 truncate">{detail}</div>}
          {setAt && (
            <div className="text-xs text-slate-500">since {relativeTime(setAt)}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {status}
        {onDisconnect && canDisconnect && (
          <Button
            variant="ghost"
            onClick={onDisconnect}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            Disconnect
          </Button>
        )}
        <Button variant="outline" onClick={onAction}>
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}

type OpsEvent = { ts: string; type: string } & Record<string, unknown>;

// Orchestrator operator events — clone resolutions, domain aliases, site
// deletions, trashed data dirs. Rare and destructive, so they belong on the
// tenant page rather than buried in container logs. Hidden entirely while
// the feed is empty or the tenant image predates the endpoint.
function OpsEventsCard({ tenantId }: { tenantId: string }) {
  const events = useQuery({
    queryKey: ['tenant-ops-events', tenantId],
    queryFn: () =>
      api.get<{ events: OpsEvent[]; unavailable?: string }>(`/api/tenants/${tenantId}/ops-events`),
    // Not polled: this reads from the tenant container, so repeating it every
    // minute would hold a stopped container awake for as long as the page sits
    // open. The probe button invalidates this key when the operator asks.
  });
  const list = events.data?.events ?? [];
  if (!list.length) return null;

  const detailsOf = (e: OpsEvent) => {
    const { ts: _ts, type: _type, ...rest } = e;
    return Object.entries(rest)
      .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
      .join(' ');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Orchestrator events</CardTitle>
      </CardHeader>
      <CardBody className="p-0">
        <ul className="divide-y divide-slate-100">
          {list.map((e, i) => (
            <li
              key={`${e.ts}-${i}`}
              className="px-5 py-2.5 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between text-sm"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-mono text-xs text-slate-700 shrink-0">{e.type}</span>
                <span className="text-xs text-slate-500 truncate">{detailsOf(e)}</span>
              </div>
              <div className="text-xs text-slate-500 whitespace-nowrap">{relativeTime(e.ts)}</div>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
